import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Icon, Link, Popover, Text, useTheme } from '@Sage/hoi-poi-ui';

import Context from 'managers/Context';
import { COMPANIES } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { isBackendFalsy } from '@Sage/ssm-utils/backendScalars';
import { formatAddress } from 'utils/addresses';

import FieldWrapper from '../FieldWrapper';
import AddressModal from './AddressModal';
import AddressSelectorRow from './AddressSelectorRow';
import { useShippingAddressPermissions } from './useShippingAddressPermissions';
import './styles.scss';

const propTypes = {
    data: PropTypes.object,
    error: PropTypes.string,
    fieldId: PropTypes.string,
    hidden: PropTypes.bool,
    label: PropTypes.string,
    mandatory: PropTypes.bool,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
};

const ShippingAddress = ({
    data,
    error,
    fieldId,
    hidden,
    label,
    mandatory,
    onChange,
    readOnly,
    shouldRenderField,
}) => {
    const theme = useTheme();
    const { canAdd, canEdit } = useShippingAddressPermissions();
    const [addresses, setAddresses] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);

    const idCompany = data?.idCompany?.value;
    const currentIdAddress = data?.idAddress;

    const getAddresses = useCallback(
        (companyId) =>
            new Promise((resolve, reject) => {
                Context.entityManager
                    .getEntitiesManager(COMPANIES)
                    .getCompanyAddress(companyId)
                    .then((result) => {
                        setAddresses(result);
                        resolve(result);
                    })
                    .catch((err) => {
                        console.error('ShippingAddress error:', err);
                        reject();
                    });
            }),
        [],
    );

    useEffect(() => {
        if (idCompany && !isBackendFalsy(idCompany)) {
            getAddresses(idCompany);
        }
    }, [idCompany, getAddresses]);

    useEffect(() => {
        setSelectedAddress(currentIdAddress);
    }, [currentIdAddress]);

    const onTogglePopover = useCallback(() => {
        if (isAddressModalOpen || readOnly) return;
        setPopoverVisible((prev) => !prev);
    }, [isAddressModalOpen, readOnly]);

    const onPopoverVisibleChange = useCallback(
        (visible) => {
            // Never close popover when modal is open
            if (isAddressModalOpen) return;
            if (readOnly) return;
            setPopoverVisible(visible);
        },
        [isAddressModalOpen, readOnly],
    );

    const selectAddress = useCallback(
        (id, addressComponents) => {
            setSelectedAddress(id);
            onChange?.({
                idAddress: id,
                address: addressComponents?.address,
                address2: addressComponents?.address2,
                city: addressComponents?.city,
                country: addressComponents?.country,
                idCountry: addressComponents?.idCountry,
                postCode: addressComponents?.postCode,
                province: addressComponents?.province,
            });
        },
        [onChange],
    );

    const onCheckAddress = useCallback(
        (id, addressComponents) => {
            selectAddress(id, addressComponents);
            setPopoverVisible(false);
        },
        [selectAddress],
    );

    const openAddressModal = useCallback(() => {
        if (!canAdd) return;
        setEditingAddress(null);
        setIsAddressModalOpen(true);
    }, [canAdd]);

    const closeAddressModal = useCallback(() => {
        setIsAddressModalOpen(false);
    }, []);

    const onSaveAddress = useCallback(
        (formData) => {
            getAddresses(idCompany)
                .then((updatedAddresses) => {
                    if (formData?.isDeleted) {
                        if (formData.id === selectedAddress) {
                            selectAddress(null, null);
                        }
                    } else if (formData?.isNew) {
                        const newAddress = updatedAddresses?.find(
                            (addr) => addr.id !== -1 && addr.description === formData.description,
                        );
                        if (newAddress) {
                            selectAddress(newAddress.id, newAddress);
                        }
                    } else if (formData?.id === selectedAddress) {
                        selectAddress(formData.id, formData);
                    }
                })
                .then(closeAddressModal)
                .catch(() => {
                    console.error('error getting addresses for the company');
                });
        },
        [closeAddressModal, idCompany, selectAddress, selectedAddress, getAddresses],
    );

    const onClickEditAddress = useCallback(
        (id) => (e) => {
            if (!canEdit) return;
            e?.preventDefault?.();
            e?.stopPropagation?.();
            const address = addresses.find((addr) => addr.id === id);
            setEditingAddress(address);
            setIsAddressModalOpen(true);
        },
        [addresses, canEdit],
    );

    const afterCloseAddressModal = useCallback(() => {
        if (editingAddress) setEditingAddress(null);
    }, [editingAddress]);

    const formattedAddress = useMemo(() => {
        if (data?.withCompanyAddress) return data.address;
        if (!data?.address) return '';

        return formatAddress(
            data.address,
            data.address2,
            data.city,
            data.province,
            data.postCode,
            data.country,
            data.idCountry,
        );
    }, [data]);

    const addressesContent = useMemo(() => {
        if (!addresses || addresses.length === 0) return null;

        const defaultAddress = addresses
            .filter((addr) => addr.id === -1)
            .map((addr) => (
                <AddressSelectorRow
                    key={addr.id}
                    name={getLiteral('label_address_account')}
                    address={addr}
                    selected={selectedAddress === addr.id}
                    onChange={onCheckAddress}
                />
            ));

        const otherAddresses = addresses
            .filter((addr) => addr.id !== -1)
            .map((addr) => (
                <AddressSelectorRow
                    key={addr.id}
                    name={addr.description}
                    address={addr}
                    selected={selectedAddress === addr.id}
                    onChange={onCheckAddress}
                    onEdit={canEdit ? onClickEditAddress(addr.id) : undefined}
                />
            ));

        return (
            <>
                <div className="fm-shipping-address__selector">
                    {defaultAddress?.length > 0 && (
                        <div className="fm-shipping-address__selector-section">
                            <Text
                                type="subtitle"
                                medium
                                className="fm-shipping-address__selector-title"
                            >
                                {getLiteral('label_main_address')}
                            </Text>
                            {defaultAddress}
                            <AddressSelectorRow
                                name={getLiteral('label_without_address')}
                                selected={!selectedAddress}
                                onChange={onCheckAddress}
                            />
                        </div>
                    )}
                    {otherAddresses?.length > 0 && (
                        <div className="fm-shipping-address__selector-section fm-shipping-address__selector-section--other">
                            <Text
                                type="subtitle"
                                medium
                                className="fm-shipping-address__selector-title"
                            >
                                {getLiteral('label_other_addresses')}
                            </Text>
                            {otherAddresses}
                        </div>
                    )}
                </div>
                {canAdd && (
                    <>
                        <Divider />
                        <Link
                            className="fm-shipping-address__add-link"
                            variation="primary"
                            type="subtitle"
                            onClick={openAddressModal}
                        >
                            <Icon name="addPlus" color={theme.colors.actionMajor[500]} />
                            {getLiteral('action_add_address')}
                        </Link>
                    </>
                )}
            </>
        );
    }, [
        addresses,
        canAdd,
        canEdit,
        selectedAddress,
        onCheckAddress,
        openAddressModal,
        onClickEditAddress,
        theme,
    ]);

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;
    if (!idCompany) return null;

    const displayLinkLabel =
        formattedAddress ||
        (canAdd ? getLiteral('action_add_address') : getLiteral('label_selectone'));

    return (
        <FieldWrapper error={error} label={label} labelMode="horizontal" mandatory={mandatory}>
            <div className="fm-shipping-address">
                <Popover
                    className="fm-shipping-address__popover"
                    placement="bottomLeft"
                    content={addressesContent}
                    trigger={[]}
                    visible={popoverVisible}
                    onVisibleChange={onPopoverVisibleChange}
                >
                    <div
                        className={`fm-shipping-address__display${readOnly ? ' fm-shipping-address__display--readonly' : ''}`}
                        onClick={onTogglePopover}
                    >
                        <Link
                            isDisabled={readOnly}
                            overrides={{
                                root: {
                                    style: {
                                        color: readOnly
                                            ? theme.colors.textLight.disabled
                                            : theme.colors.actionMajor[500],
                                    },
                                },
                            }}
                        >
                            {displayLinkLabel}
                        </Link>
                        <Icon
                            name={readOnly ? 'lock' : 'edit'}
                            color={
                                readOnly
                                    ? theme.colors.textLight.disabled
                                    : theme.colors.semantic.positive500
                            }
                        />
                    </div>
                </Popover>
                <AddressModal
                    canAdd={canAdd}
                    canEdit={canEdit}
                    idCompany={idCompany}
                    isOpen={isAddressModalOpen}
                    onRequestClose={closeAddressModal}
                    onCancel={closeAddressModal}
                    onConfirm={onSaveAddress}
                    edit={editingAddress}
                    onAfterClose={afterCloseAddressModal}
                />
            </div>
        </FieldWrapper>
    );
};

ShippingAddress.displayName = 'ShippingAddress';
ShippingAddress.propTypes = propTypes;

export default memo(ShippingAddress);
